/**
 * Customers Management Page.
 *
 * Full customer CRUD with multi-contact management. Each customer can have
 * multiple contacts with different roles (buyer, AP, owner, etc.).
 * Designed to be embedded in the Sales Hub.
 */

import { useState } from "react";
import {
  Plus,
  Trash2,
  UserCircle,
  UserPlus,
  Users,
  Phone,
  Mail,
  Globe,
  Star,
  X,
  Edit2,
  Power,
  PowerOff,
  FileCheck,
  AlertTriangle,
  Shield,
  Tag,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import type { PaginatedResponse } from "@/types/common";
import type {
  Customer,
  CustomerContact,
  TaxCertificate,
} from "@/types/sales-order";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const DEFAULT_CERT_TYPES = [
  { value: "ag_exemption", label: "Agricultural Exemption" },
  { value: "resale_cert", label: "Resale Certificate" },
  { value: "interstate_form", label: "Interstate/Multijurisdiction Form" },
  { value: "government", label: "Government Exemption" },
];

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

function CustomersPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<PaginatedResponse<Customer>>({
    queryKey: ["customers"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/customers?page_size=100"))
        .data as PaginatedResponse<Customer>,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);
  const [expandedCerts, setExpandedCerts] = useState<string | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [custEmail, setCustEmail] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [taxId, setTaxId] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setName("");
    setCompanyName("");
    setCustEmail("");
    setCustPhone("");
    setWebsite("");
    setTaxId("");
    setPaymentTerms("");
    setNotes("");
  };

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/customers", {
          name,
          company_name: companyName || undefined,
          email: custEmail || undefined,
          phone: custPhone || undefined,
          website: website || undefined,
          tax_id: taxId || undefined,
          payment_terms: paymentTerms || undefined,
          notes: notes || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      setShowCreate(false);
      resetForm();
      toast.success("Customer created", `${name} has been added.`);
    },
    onError: () =>
      toast.error("Failed to create customer", "Please try again."),
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/customers/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Customer deactivated");
    },
    onError: () => toast.error("Failed to deactivate customer"),
  });

  const reactivateMutation = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.put(`/api/v1/customers/${id}`, { is_active: true }))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Customer reactivated");
    },
    onError: () => toast.error("Failed to reactivate customer"),
  });

  const startEdit = (c: Customer) => {
    setEditingCustomer(c.id);
    setName(c.name);
    setCompanyName(c.company_name || "");
    setCustEmail(c.email || "");
    setCustPhone(c.phone || "");
    setWebsite(c.website || "");
    setTaxId(c.tax_id || "");
    setPaymentTerms(c.payment_terms || "");
    setNotes(c.notes || "");
  };
  const cancelEdit = () => {
    setEditingCustomer(null);
    resetForm();
  };

  const customers = data?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Customers
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Manage customers, contacts, and tax certificates.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setShowCreate(!showCreate);
            setEditingCustomer(null);
          }}
        >
          {showCreate ? (
            "Cancel"
          ) : (
            <>
              <Plus className="h-4 w-4" /> Add Customer
            </>
          )}
        </Button>
      </div>

      {showCreate && (
        <Card>
          <h3 className="mb-4 text-sm font-medium text-[var(--text-primary)]">
            New customer
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-3"
          >
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="John Smith"
            />
            <Input
              label="Company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Smith Farms LLC"
            />
            <Input
              label="Email"
              type="email"
              value={custEmail}
              onChange={(e) => setCustEmail(e.target.value)}
              placeholder="john@smithfarms.com"
            />
            <Input
              label="Phone"
              value={custPhone}
              onChange={(e) => setCustPhone(e.target.value)}
              placeholder="555-123-4567"
            />
            <Input
              label="Website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://smithfarms.com"
            />
            <Input
              label="Tax ID / EIN"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              placeholder="12-3456789"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Payment terms
              </label>
              <select
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                className="mt-1.5 block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="">Select...</option>
                <option value="COD">COD</option>
                <option value="Due on Receipt">Due on receipt</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
                <option value="Net 60">Net 60</option>
                <option value="Prepaid">Prepaid</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <Input
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
            <div className="sm:col-span-3 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Create customer
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : customers.length > 0 ? (
        <div className="space-y-3 stagger-children">
          {customers.map((cust) => {
            const isExpanded = expandedCustomer === cust.id;
            const isEditing = editingCustomer === cust.id;
            return (
              <div key={cust.id}>
                <Card
                  padding="none"
                  className={!cust.is_active ? "opacity-60" : ""}
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--fill-info)] text-[var(--text-info)]">
                      <UserCircle className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[var(--text-primary)]">
                          {cust.name}
                        </span>
                        {cust.company_name && (
                          <span className="text-xs text-[var(--text-tertiary)]">
                            ({cust.company_name})
                          </span>
                        )}
                        <Badge variant={cust.is_active ? "success" : "default"}>
                          {cust.is_active ? "Active" : "Inactive"}
                        </Badge>
                        {cust.payment_terms && (
                          <Badge variant="info">{cust.payment_terms}</Badge>
                        )}
                        {cust.tax_exempt && (
                          <Badge variant="warning">
                            <Shield className="h-3 w-3 mr-0.5" />
                            Tax Exempt
                          </Badge>
                        )}
                        {cust.tax_tags?.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300"
                          >
                            <Tag className="h-2.5 w-2.5" />
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-[var(--text-tertiary)] flex-wrap">
                        {cust.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {cust.email}
                          </span>
                        )}
                        {cust.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {cust.phone}
                          </span>
                        )}
                        {cust.website && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            {cust.website}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          setExpandedCustomer(isExpanded ? null : cust.id);
                          if (!isExpanded) setExpandedCerts(null);
                        }}
                        className={`flex items-center gap-1.5 rounded-[var(--radius-md)] border px-3 py-1.5 text-xs font-medium transition-all ${
                          isExpanded
                            ? "border-[var(--color-interactive-primary)]/30 bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                            : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--color-interactive-primary)]/30 hover:text-[var(--color-interactive-primary)]"
                        }`}
                      >
                        <Users className="h-3.5 w-3.5" /> Contacts
                      </button>
                      <button
                        onClick={() => {
                          setExpandedCerts(
                            expandedCerts === cust.id ? null : cust.id,
                          );
                          if (expandedCerts !== cust.id)
                            setExpandedCustomer(null);
                        }}
                        className={`flex items-center gap-1.5 rounded-[var(--radius-md)] border px-3 py-1.5 text-xs font-medium transition-all ${
                          expandedCerts === cust.id
                            ? "border-amber-400/30 bg-amber-50 text-amber-700 dark:bg-amber-900/10 dark:text-amber-300"
                            : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-amber-400/30 hover:text-amber-700"
                        }`}
                      >
                        <FileCheck className="h-3.5 w-3.5" /> Certificates
                      </button>
                      <button
                        onClick={() =>
                          isEditing ? cancelEdit() : startEdit(cust)
                        }
                        title="Edit customer"
                        className={`flex items-center justify-center h-8 w-8 rounded-[var(--radius-md)] border transition-colors ${
                          isEditing
                            ? "border-[var(--color-interactive-primary)]/30 bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                            : "border-[var(--border-default)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)]"
                        }`}
                      >
                        {isEditing ? (
                          <X className="h-3.5 w-3.5" />
                        ) : (
                          <Edit2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                      {cust.is_active ? (
                        <button
                          onClick={() => deactivateMutation.mutate(cust.id)}
                          title="Deactivate"
                          className="flex items-center justify-center h-8 w-8 rounded-[var(--radius-md)] border border-[var(--border-default)] text-[var(--text-tertiary)] transition-colors hover:border-[var(--text-danger)]/30 hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
                        >
                          <PowerOff className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => reactivateMutation.mutate(cust.id)}
                          title="Reactivate"
                          className="flex items-center justify-center h-8 w-8 rounded-[var(--radius-md)] border border-[var(--border-default)] text-[var(--text-tertiary)] transition-colors hover:border-[var(--text-success)]/30 hover:text-[var(--text-success)] hover:bg-[var(--fill-success)]"
                        >
                          <Power className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteTarget(cust.id)}
                        title="Permanently delete"
                        className="flex items-center justify-center h-8 w-8 rounded-[var(--radius-md)] border border-[var(--border-default)] text-[var(--text-tertiary)] transition-colors hover:border-[var(--text-danger)]/30 hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="border-t border-[var(--border-default)] px-4 py-3">
                      <CustomerEditForm
                        customerId={cust.id}
                        name={name}
                        setName={setName}
                        companyName={companyName}
                        setCompanyName={setCompanyName}
                        email={custEmail}
                        setEmail={setCustEmail}
                        phone={custPhone}
                        setPhone={setCustPhone}
                        website={website}
                        setWebsite={setWebsite}
                        taxId={taxId}
                        setTaxId={setTaxId}
                        paymentTerms={paymentTerms}
                        setPaymentTerms={setPaymentTerms}
                        notes={notes}
                        setNotes={setNotes}
                        onDone={() => {
                          cancelEdit();
                          qc.invalidateQueries({ queryKey: ["customers"] });
                        }}
                      />
                    </div>
                  )}

                  {isExpanded && (
                    <div className="border-t border-[var(--border-default)] px-4 py-3 bg-[var(--surface-base)]/50">
                      <CustomerContacts customerId={cust.id} />
                    </div>
                  )}

                  {expandedCerts === cust.id && (
                    <div className="border-t border-[var(--border-default)] px-4 py-3 bg-amber-50/30 dark:bg-amber-900/5">
                      <CustomerCertificates customerId={cust.id} />
                    </div>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <UserCircle />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No customers
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Add your first customer to start tracking buyers.
            </p>
          </div>
        </Card>
      )}

      <ConfirmDeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        apiPath="/api/v1/customers"
        entityId={deleteTarget || ""}
        queryKey="customers"
      />
    </div>
  );
}

/* ======================== Inline Edit Form ======================== */

function CustomerEditForm({
  customerId,
  name,
  setName,
  companyName,
  setCompanyName,
  email,
  setEmail,
  phone,
  setPhone,
  website,
  setWebsite,
  taxId,
  setTaxId,
  paymentTerms,
  setPaymentTerms,
  notes,
  setNotes,
  onDone,
}: {
  customerId: string;
  onDone: () => void;
  name: string;
  setName: (v: string) => void;
  companyName: string;
  setCompanyName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  website: string;
  setWebsite: (v: string) => void;
  taxId: string;
  setTaxId: (v: string) => void;
  paymentTerms: string;
  setPaymentTerms: (v: string) => void;
  notes: string;
  setNotes: (v: string) => void;
}) {
  const updateMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.put(`/api/v1/customers/${customerId}`, {
          name,
          company_name: companyName || undefined,
          email: email || undefined,
          phone: phone || undefined,
          website: website || undefined,
          tax_id: taxId || undefined,
          payment_terms: paymentTerms || undefined,
          notes: notes || undefined,
        })
      ).data,
    onSuccess: () => {
      onDone();
      toast.success("Customer updated");
    },
    onError: () => toast.error("Failed to update customer"),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        updateMutation.mutate();
      }}
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      <Input
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <Input
        label="Company"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
      />
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Input
        label="Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
      />
      <Input
        label="Website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />
      <Input
        label="Tax ID / EIN"
        value={taxId}
        onChange={(e) => setTaxId(e.target.value)}
      />
      <div>
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          Payment terms
        </label>
        <select
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
          className="mt-1.5 block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
        >
          <option value="">Select...</option>
          <option value="COD">COD</option>
          <option value="Due on Receipt">Due on receipt</option>
          <option value="Net 15">Net 15</option>
          <option value="Net 30">Net 30</option>
          <option value="Net 45">Net 45</option>
          <option value="Net 60">Net 60</option>
          <option value="Prepaid">Prepaid</option>
        </select>
      </div>
      <div className="sm:col-span-2">
        <Input
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div className="sm:col-span-3 flex justify-end">
        <Button type="submit" size="sm" isLoading={updateMutation.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

/* ======================== Customer Contacts ======================== */

function CustomerContacts({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const { data: customer, isLoading } = useQuery<Customer>({
    queryKey: ["customer", customerId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/customers/${customerId}`)).data as Customer,
  });

  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [cName, setCName] = useState("");
  const [cTitle, setCTitle] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cMobile, setCMobile] = useState("");
  const [cPrimary, setCPrimary] = useState(false);
  const [cNotes, setCNotes] = useState("");

  const reset = () => {
    setCName("");
    setCTitle("");
    setCEmail("");
    setCPhone("");
    setCMobile("");
    setCPrimary(false);
    setCNotes("");
  };
  const startEditContact = (c: CustomerContact) => {
    setEditingId(c.id);
    setShowAdd(false);
    setCName(c.name);
    setCTitle(c.title || "");
    setCEmail(c.email || "");
    setCPhone(c.phone || "");
    setCMobile(c.mobile || "");
    setCPrimary(c.is_primary);
    setCNotes(c.notes || "");
  };
  const cancelContactEdit = () => {
    setEditingId(null);
    reset();
  };

  const addMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(`/api/v1/customers/${customerId}/contacts`, {
          name: cName,
          title: cTitle || undefined,
          email: cEmail || undefined,
          phone: cPhone || undefined,
          mobile: cMobile || undefined,
          is_primary: cPrimary,
          notes: cNotes || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      setShowAdd(false);
      reset();
      toast.success("Contact added", `${cName} has been added.`);
    },
    onError: () => toast.error("Failed to add contact"),
  });
  const updateContactMutation = useMutation({
    mutationFn: async (contactId: string) =>
      (
        await apiClient.put(
          `/api/v1/customers/${customerId}/contacts/${contactId}`,
          {
            name: cName,
            title: cTitle || undefined,
            email: cEmail || undefined,
            phone: cPhone || undefined,
            mobile: cMobile || undefined,
            is_primary: cPrimary,
            notes: cNotes || undefined,
          },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      cancelContactEdit();
      toast.success("Contact updated");
    },
    onError: () => toast.error("Failed to update contact"),
  });
  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await apiClient.delete(
        `/api/v1/customers/${customerId}/contacts/${contactId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      toast.success("Contact deleted");
    },
    onError: () => toast.error("Failed to delete contact"),
  });
  const setPrimaryMutation = useMutation({
    mutationFn: async (contactId: string) =>
      (
        await apiClient.put(
          `/api/v1/customers/${customerId}/contacts/${contactId}`,
          { is_primary: true },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      toast.success("Primary contact updated");
    },
    onError: () => toast.error("Failed to set primary contact"),
  });

  if (isLoading || !customer) return <LoadingSpinner className="py-4" />;
  const contacts = customer.contacts || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-[9px] font-medium uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
          Contacts ({contacts.length})
        </h4>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setShowAdd(!showAdd);
            cancelContactEdit();
          }}
        >
          {showAdd ? (
            <>
              <X className="h-3 w-3" /> Cancel
            </>
          ) : (
            <>
              <UserPlus className="h-3 w-3" /> Add contact
            </>
          )}
        </Button>
      </div>

      {showAdd && (
        <ContactForm
          onSubmit={() => addMutation.mutate()}
          isPending={addMutation.isPending}
          submitLabel="Add contact"
          name={cName}
          setName={setCName}
          title={cTitle}
          setTitle={setCTitle}
          email={cEmail}
          setEmail={setCEmail}
          phone={cPhone}
          setPhone={setCPhone}
          mobile={cMobile}
          setMobile={setCMobile}
          isPrimary={cPrimary}
          setIsPrimary={setCPrimary}
          notes={cNotes}
          setNotes={setCNotes}
        />
      )}

      {contacts.length > 0 ? (
        <div className="space-y-1.5">
          {contacts.map((contact) => (
            <Card key={contact.id} padding="sm" className="hover-lift">
              {editingId === contact.id ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-[var(--text-secondary)]">
                      Editing contact
                    </span>
                    <button
                      onClick={cancelContactEdit}
                      className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <ContactForm
                    onSubmit={() => updateContactMutation.mutate(contact.id)}
                    isPending={updateContactMutation.isPending}
                    submitLabel="Save contact"
                    name={cName}
                    setName={setCName}
                    title={cTitle}
                    setTitle={setCTitle}
                    email={cEmail}
                    setEmail={setCEmail}
                    phone={cPhone}
                    setPhone={setCPhone}
                    mobile={cMobile}
                    setMobile={setCMobile}
                    isPrimary={cPrimary}
                    setIsPrimary={setCPrimary}
                    notes={cNotes}
                    setNotes={setCNotes}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                      contact.is_primary
                        ? "bg-[var(--color-interactive-primary)]/12 text-[var(--color-interactive-primary)]"
                        : "bg-[var(--surface-sunken)] text-[var(--text-secondary)]"
                    }`}
                  >
                    {contact.name
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {contact.name}
                      </span>
                      {contact.is_primary && (
                        <span className="flex items-center gap-0.5 text-[10px] font-medium text-[var(--color-interactive-primary)]">
                          <Star className="h-3 w-3 fill-current" /> Primary
                        </span>
                      )}
                      {contact.title && (
                        <span className="text-xs text-[var(--text-tertiary)]">
                          · {contact.title}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[var(--text-tertiary)] flex-wrap">
                      {contact.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {contact.email}
                        </span>
                      )}
                      {contact.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {contact.phone}
                        </span>
                      )}
                      {contact.mobile && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {contact.mobile}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    {!contact.is_primary && (
                      <button
                        onClick={() => setPrimaryMutation.mutate(contact.id)}
                        title="Set as primary"
                        className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--color-interactive-primary)] hover:bg-[var(--color-interactive-primary)]/10 transition-colors"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => startEditContact(contact)}
                      title="Edit"
                      className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate(contact.id)}
                      title="Delete"
                      className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)] transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-center text-xs text-[var(--text-tertiary)] py-3">
          No contacts yet — add one above.
        </p>
      )}
    </div>
  );
}

/* ======================== Reusable Contact Form ======================== */

function ContactForm({
  onSubmit,
  isPending,
  submitLabel,
  name,
  setName,
  title,
  setTitle,
  email,
  setEmail,
  phone,
  setPhone,
  mobile,
  setMobile,
  isPrimary,
  setIsPrimary,
  notes,
  setNotes,
}: {
  onSubmit: () => void;
  isPending: boolean;
  submitLabel: string;
  name: string;
  setName: (v: string) => void;
  title: string;
  setTitle: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  mobile: string;
  setMobile: (v: string) => void;
  isPrimary: boolean;
  setIsPrimary: (v: boolean) => void;
  notes: string;
  setNotes: (v: string) => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      <Input
        label="Full name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        placeholder="Jane Smith"
      />
      <Input
        label="Title / role"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Purchasing Manager"
      />
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="jane@customer.com"
      />
      <Input
        label="Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="555-123-4567"
      />
      <Input
        label="Mobile"
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
        placeholder="555-987-6543"
      />
      <div className="flex items-end">
        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="rounded border-[var(--border-default)]"
          />
          Primary contact
        </label>
      </div>
      <div className="sm:col-span-3">
        <Input
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes"
        />
      </div>
      <div className="sm:col-span-3 flex justify-end">
        <Button type="submit" size="sm" isLoading={isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

/* ======================== Customer Certificates ======================== */

function CustomerCertificates({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const { data: certs, isLoading } = useQuery<TaxCertificate[]>({
    queryKey: ["tax-certificates", customerId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/tax-certificates?customer_id=${customerId}`,
        )
      ).data as TaxCertificate[],
  });

  const [showAdd, setShowAdd] = useState(false);
  const [certType, setCertType] = useState("");
  const [certNumber, setCertNumber] = useState("");
  const [issuingState, setIssuingState] = useState("");
  const [issuedDate, setIssuedDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [certNotes, setCertNotes] = useState("");
  const [customCertTypes, setCustomCertTypes] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [customCertInput, setCustomCertInput] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Build combined cert types: defaults + custom + any from existing certs.
  const allCertTypes = (() => {
    const combined = [...DEFAULT_CERT_TYPES, ...customCertTypes];
    const known = new Set(combined.map((c) => c.value));
    for (const c of certs || []) {
      if (c.certificate_type && !known.has(c.certificate_type)) {
        combined.push({ value: c.certificate_type, label: c.certificate_type });
        known.add(c.certificate_type);
      }
    }
    return combined;
  })();

  const reset = () => {
    setCertType("");
    setCertNumber("");
    setIssuingState("");
    setIssuedDate("");
    setExpirationDate("");
    setCertNotes("");
    setShowCustomInput(false);
    setCustomCertInput("");
  };

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/tax-certificates", {
          customer_id: customerId,
          certificate_type: certType,
          certificate_number: certNumber || undefined,
          issuing_state: issuingState || undefined,
          issued_date: issuedDate || undefined,
          expiration_date: expirationDate || undefined,
          notes: certNotes || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tax-certificates", customerId] });
      setShowAdd(false);
      reset();
      toast.success("Certificate added");
    },
    onError: () => toast.error("Failed to add certificate"),
  });

  const revokeMutation = useMutation({
    mutationFn: async (certId: string) =>
      (
        await apiClient.put(`/api/v1/tax-certificates/${certId}`, {
          is_revoked: true,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tax-certificates", customerId] });
      toast.success("Certificate revoked");
    },
    onError: () => toast.error("Failed to revoke certificate"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (certId: string) => {
      await apiClient.delete(`/api/v1/tax-certificates/${certId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tax-certificates", customerId] });
      toast.success("Certificate deleted");
    },
    onError: () => toast.error("Failed to delete certificate"),
  });

  if (isLoading) return <LoadingSpinner className="py-4" />;
  const certificates = certs || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-[9px] font-medium uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
          Tax Certificates ({certificates.length})
        </h4>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setShowAdd(!showAdd)}
        >
          {showAdd ? (
            <>
              <X className="h-3 w-3" /> Cancel
            </>
          ) : (
            <>
              <Plus className="h-3 w-3" /> Add certificate
            </>
          )}
        </Button>
      </div>

      {showAdd && (
        <Card padding="sm">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
            className="grid grid-cols-1 gap-3 sm:grid-cols-3"
          >
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Certificate Type
              </label>
              <select
                value={
                  allCertTypes.some((c) => c.value === certType)
                    ? certType
                    : certType
                      ? "__custom__"
                      : ""
                }
                onChange={(e) => {
                  if (e.target.value === "__add__") {
                    setShowCustomInput(true);
                  } else if (e.target.value !== "__custom__") {
                    setCertType(e.target.value);
                    setShowCustomInput(false);
                  }
                }}
                required={!showCustomInput}
                className="mt-1.5 block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="">Select type...</option>
                {allCertTypes.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
                {certType &&
                  !allCertTypes.some((c) => c.value === certType) && (
                    <option value="__custom__">{certType}</option>
                  )}
                <option value="__add__">+ Add custom type...</option>
              </select>
              {showCustomInput && (
                <div className="mt-1.5 flex gap-1.5">
                  <input
                    type="text"
                    value={customCertInput}
                    onChange={(e) => setCustomCertInput(e.target.value)}
                    placeholder="Enter certificate type name"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (customCertInput.trim()) {
                          const val = customCertInput
                            .trim()
                            .toLowerCase()
                            .replace(/\s+/g, "_");
                          const label = customCertInput.trim();
                          if (!allCertTypes.some((c) => c.value === val)) {
                            setCustomCertTypes((prev) => [
                              ...prev,
                              { value: val, label },
                            ]);
                          }
                          setCertType(val);
                          setCustomCertInput("");
                          setShowCustomInput(false);
                        }
                      }
                    }}
                    className="block w-full h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (customCertInput.trim()) {
                        const val = customCertInput
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, "_");
                        const label = customCertInput.trim();
                        if (!allCertTypes.some((c) => c.value === val)) {
                          setCustomCertTypes((prev) => [
                            ...prev,
                            { value: val, label },
                          ]);
                        }
                        setCertType(val);
                        setCustomCertInput("");
                        setShowCustomInput(false);
                      }
                    }}
                    className="shrink-0 h-8 px-3 rounded-[var(--radius-md)] bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 transition-colors"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomCertInput("");
                    }}
                    className="shrink-0 h-8 px-2 text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <Input
              label="Certificate Number"
              value={certNumber}
              onChange={(e) => setCertNumber(e.target.value)}
              placeholder="e.g., AG-123456"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Issuing State
              </label>
              <select
                value={issuingState}
                onChange={(e) => setIssuingState(e.target.value)}
                className="mt-1.5 block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="">Select state...</option>
                {US_STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Issued Date"
              type="date"
              value={issuedDate}
              onChange={(e) => setIssuedDate(e.target.value)}
            />
            <Input
              label="Expiration Date"
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
            />
            <Input
              label="Notes"
              value={certNotes}
              onChange={(e) => setCertNotes(e.target.value)}
              placeholder="Optional"
            />
            <div className="sm:col-span-3 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Add Certificate
              </Button>
            </div>
          </form>
        </Card>
      )}

      {certificates.length > 0 ? (
        <div className="space-y-1.5">
          {certificates.map((cert) => {
            const isExpired =
              cert.expiration_date &&
              new Date(cert.expiration_date) < new Date();
            const isExpiringSoon =
              cert.expiration_date &&
              !isExpired &&
              new Date(cert.expiration_date).getTime() - Date.now() <
                30 * 24 * 60 * 60 * 1000;
            const label =
              allCertTypes.find((c) => c.value === cert.certificate_type)
                ?.label || cert.certificate_type;

            return (
              <Card key={cert.id} padding="sm">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs ${
                      cert.is_revoked || isExpired
                        ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300"
                        : isExpiringSoon
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                          : "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                    }`}
                  >
                    {cert.is_revoked || isExpired ? (
                      <AlertTriangle className="h-4 w-4" />
                    ) : (
                      <FileCheck className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[var(--text-primary)]">
                        {label}
                      </span>
                      {cert.certificate_number && (
                        <span className="text-xs text-[var(--text-tertiary)]">
                          #{cert.certificate_number}
                        </span>
                      )}
                      {cert.is_revoked ? (
                        <Badge variant="danger">Revoked</Badge>
                      ) : isExpired ? (
                        <Badge variant="danger">Expired</Badge>
                      ) : isExpiringSoon ? (
                        <Badge variant="warning">Expiring Soon</Badge>
                      ) : (
                        <Badge variant="success">Current</Badge>
                      )}
                      {cert.issuing_state && (
                        <Badge variant="default">{cert.issuing_state}</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                      {cert.issued_date && (
                        <span>
                          Issued:{" "}
                          {new Date(cert.issued_date).toLocaleDateString()}
                        </span>
                      )}
                      {cert.expiration_date && (
                        <span className="ml-3">
                          Expires:{" "}
                          {new Date(cert.expiration_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    {!cert.is_revoked && cert.is_current && (
                      <button
                        onClick={() => {
                          if (confirm("Revoke this certificate?"))
                            revokeMutation.mutate(cert.id);
                        }}
                        title="Revoke"
                        className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors"
                      >
                        <Shield className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (confirm("Delete this certificate?"))
                          deleteMutation.mutate(cert.id);
                      }}
                      title="Delete"
                      className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)] transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-xs text-[var(--text-tertiary)] py-3">
          No certificates on file — add one above.
        </p>
      )}
    </div>
  );
}

export default CustomersPage;
